
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TrendingDown, BarChart, TrendingUp } from "lucide-react";
import { MetalPrice } from "@/lib/metal-price";
import { calculateMinPrice, calculateMaxPrice, calculateAveragePrice, formatPrice } from "@/lib/utils/priceStatistics";
import { cn } from "@/lib/utils";

interface MetalStatsCardsProps {
  data: MetalPrice[];
  metalType: 'gold' | 'silver';
  isLoading?: boolean;
}

export function MetalStatsCards({ data, metalType, isLoading = false }: MetalStatsCardsProps) {
  const minPrice = calculateMinPrice(data);
  const maxPrice = calculateMaxPrice(data);
  const avgPrice = calculateAveragePrice(data);

  const goldTheme = "text-gold border-gold/20 bg-gold/5";
  const silverTheme = "text-silver border-silver/20 bg-silver/5";
  const themeClass = metalType === 'gold' ? goldTheme : silverTheme;

  const stats = [
    {
      title: "Minimo",
      value: formatPrice(minPrice),
      icon: TrendingDown,
      description: "Prezzo più basso del periodo"
    },
    {
      title: "Media",
      value: formatPrice(avgPrice),
      icon: BarChart,
      description: "Prezzo medio del periodo"
    },
    {
      title: "Massimo",
      value: formatPrice(maxPrice),
      icon: TrendingUp,
      description: "Prezzo più alto del periodo"
    }
  ];

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
        {[1, 2, 3].map((i) => (
          <Card key={i} className={cn("animate-pulse", themeClass)}>
            <CardHeader className="pb-2">
              <div className="h-4 bg-gray-200 rounded w-16"></div>
            </CardHeader>
            <CardContent>
              <div className="h-6 bg-gray-200 rounded w-20"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
      {stats.map((stat, index) => {
        const Icon = stat.icon;
        return (
          <Card key={index} className={cn("transition-all duration-200 hover:shadow-md", themeClass)}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {stat.title}
              </CardTitle>
              <Icon className="h-4 w-4" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stat.value}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {stat.description}
              </p>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
